<script setup lang="ts">
import { DownArrowIcon, PlusIcon } from '#shared/components/icons';
import { SpoilerTransition } from '#shared/components/transitions';
import { ref, watch } from 'vue';

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    variant?: 'list' | 'card';
    size?: 'sm' | 'md';
    contentSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  }>(),
  {
    modelValue: false,
    variant: 'list',
    size: 'md',
    contentSize: 'md',
  }
);
const emit = defineEmits<{
  (e: 'update:model-value', modelValue: boolean): void;
}>();

const isExpanded = ref(false);
watch(
  () => props.modelValue,
  (newValue) => {
    isExpanded.value = newValue;
  }
);
function toggleExpandedState() {
  const newValue = !isExpanded.value;
  isExpanded.value = newValue;
  emit('update:model-value', newValue);
}
</script>

<template>
  <div
    :class="[
      'spoiler',
      `spoiler_variant_${props.variant}`,
      `spoiler_size_${props.size}`,
    ]"
    @click="toggleExpandedState"
  >
    <div :class="['spoiler__header', { spoiler__header_expanded: isExpanded }]">
      <div><slot name="header"></slot></div>
      <AppButton variant="link">
        <DownArrowIcon
          v-if="props.variant === 'list'"
          :class="['spoiler__icon', { spoiler__icon_rotated: isExpanded }]"
        />
        <PlusIcon
          v-else
          :class="['spoiler__icon', { spoiler__icon_rotated: isExpanded }]"
        />
      </AppButton>
    </div>
    <SpoilerTransition>
      <div
        v-show="isExpanded"
        :class="['spoiler__content', `spoiler__content_${props.contentSize}`]"
        @click.prevent
      >
        <slot></slot>
      </div>
    </SpoilerTransition>
  </div>
</template>

<style scoped lang="scss">
@import '#shared/assets/core.scss';

.spoiler {
  cursor: pointer;

  &_variant_list {
    padding: 26px 0;
    border-bottom: 1px solid #e5e5e5;

    @include media-breakpoint-down(sm) {
      padding: 14px 0;
    }
  }

  &_variant_card {
    background: #f7f7f8;

    &.spoiler_size_md {
      padding: 32px;
      border-radius: 24px;

      @include media-breakpoint-down(md) {
        padding: 19px 16px;
        border-radius: 16px;
      }
    }

    &.spoiler_size_sm {
      padding: 25px 23px;
      border-radius: 12px;

      .spoiler__header {
        font-size: 17px;
        line-height: 22px;
      }
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 600;
    font-size: stretch-size(375, 1920, 15, 24);
    line-height: stretch-size(375, 1920, 20, 28);
    transition: all 0.5s ease;

    &_expanded {
      margin-bottom: 24px;
    }
  }

  &_variant_list .spoiler__icon {
    width: 24px;
    height: 24px;
    color: var(--app-color-tertiary);
    transition: all 0.5s ease;

    &_rotated {
      transform: rotate(180deg);
    }
  }

  &_variant_card .spoiler__icon {
    width: 28px;
    height: 28px;
    color: #1d1d24;
    transition: all 0.5s ease;

    &_rotated {
      transform: rotate(45deg);
    }
  }

  &__content {
    font-size: 17px;
    line-height: 24px;
    cursor: auto;

    @include media-breakpoint-down(sm) {
      font-size: 15px;
      line-height: 20px;
    }

    &_xs {
      max-height: 50px;

      @include media-breakpoint-down(md) {
        max-height: 150px;
      }
    }

    &_sm {
      max-height: 100px;

      @include media-breakpoint-down(md) {
        max-height: 200px;
      }
    }

    &_md {
      max-height: 150px;

      @include media-breakpoint-down(md) {
        max-height: 250px;
      }
    }

    &_lg {
      max-height: 200px;

      @include media-breakpoint-down(md) {
        max-height: 300px;
      }
    }

    &_xl {
      max-height: 250px;

      @include media-breakpoint-down(md) {
        max-height: 350px;
      }
    }
  }
}
</style>
